import { useTranslation } from '@eti/providers';
import { Stack, Text, textVariants } from '@etg/wings';
import { css } from '@eti/styles';
import ContactMethods from '../../../../../common/components/ContactMethods';
import ContactUsDrawerButton from '../common/ContactUsDrawerButton';
import { RebookEligibility, type RebookEligibilityType } from './models';

const descriptionStyles = css`
  padding-bottom: 16px;
`;

const descriptionTextStyles = css`
  & li {
    list-style-type: disc;
    margin-inline-start: 16px;
  }
`;

const getTextsPerEligibilityType = (type: RebookEligibilityType) => {
  switch (type) {
    case RebookEligibility.CHANGE_BY_ETG:
    case RebookEligibility.CHANGE_BY_ETG_ANCILLARIES:
      return {
        heading: 'OrderDetails.ManageTrip.ChangeableTicket.Title',
        firstDescription: 'OrderDetails.ManageTrip.WhatWillCost.ChangeYourSelf.Description',
      };
    case RebookEligibility.CHANGE_FLEXIBLE_TICKET:
      return {
        heading: 'OrderDetails.ManageTrip.FlexibleTicket.ContactUs.Title',
        firstDescription: 'OrderDetails.ManageTrip.FlexibleTicket.ContactUs.Description',
        secondDescription: 'OrderDetails.ManageTrip.HowDoIChange.FlexibleTicket.Description',
      };
    case RebookEligibility.CHANGE_UNKNOWN:
      return {
        heading: 'OrderDetails.ManageTrip.GetInContact.Title',
        secondDescription: 'OrderDetails.ManageTrip.HowDoIChange.ChangeUnknown.Description',
      };
    default:
      return {};
  }
};

interface OrderChangeabilityContactUsProps {
  rebookEligibilityType: RebookEligibilityType;
  shouldShowContactMethods?: boolean;
}

const OrderChangeabilityContactUs = ({
  rebookEligibilityType,
  shouldShowContactMethods = true,
}: OrderChangeabilityContactUsProps) => {
  const { t } = useTranslation();

  const {
    heading = null,
    firstDescription = null,
    secondDescription = null,
  } = getTextsPerEligibilityType(rebookEligibilityType);

  return (
    <Stack data-testid="order-changeability-contact-us">
      <Text variant={textVariants._16_BOLD}>
        {t(heading) || t('OrderDetails.ManageTrip.ContactUsToKnow.Title')}
      </Text>
      <Stack spacing={8}>
        <Text variant={textVariants._14_BOLD}>
          {t('OrderDetails.ManageTrip.WhatWillCost.Header')}
        </Text>
        <Text asHtml className={descriptionTextStyles}>
          {t(firstDescription) || t('OrderDetails.ManageTrip.WhatWillCost.Description')}
        </Text>
      </Stack>
      <Stack spacing={8}>
        <Text variant={textVariants._14_BOLD}>
          {t('OrderDetails.ManageTrip.HowDoIChange.Header')}
        </Text>
        <Text className={descriptionStyles}>
          {t(secondDescription) || t('OrderDetails.ManageTrip.HowDoIChange.Description')}
        </Text>
        {shouldShowContactMethods && rebookEligibilityType !== RebookEligibility.CHANGE_UNKNOWN && (
          <ContactMethods chatType="oriana" />
        )}
        {shouldShowContactMethods && rebookEligibilityType === RebookEligibility.CHANGE_UNKNOWN && (
          <ContactUsDrawerButton
            chatType="oriana"
            snowplow={{
              property: 'RESCHEDULE',
              label: 'CONTACT_US',
              category: 'Manage Trip',
            }}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default OrderChangeabilityContactUs;
